import useHintModals from '@/hooks/useHintModals';
import { faFolder, faFolderOpen } from '@fortawesome/sharp-solid-svg-icons';
import type { TableRow } from '@lib/sc-eone-ui/src/table/nested_table';
import { SCNestedTable } from '@lib/sc-eone-ui/src/table/nested_table';
import { FormString } from 'eone-ui/forms/form-string';
import { Modal } from 'eone-ui/modal/modal';
import { ModalButtonType } from 'eone-ui/modal/modal-constants';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import './styles.css';

export function SCDirectoryModal({
  Value,
  DirectoriesField,
  DirectoryRowIdField,
  LoadingDirectoriesField,
  SubRowsField,
  IdField,
  Result,
  Close,
  Title,
  //IsFile,
}: {
  Value: string;
  DirectoriesField: string;
  LoadingDirectoriesField: string;
  DirectoryRowIdField: string;
  SubRowsField: string;
  IdField: string;
  Title: string;
  //IsFile: boolean;
  Result: (newDirectory: string) => void;
  Close: () => void;
}) {
  const IsFile = false;
  const [name, setName] = useState('');
  const [selectedItem, setSelectedItem] = useState<TableRow | null>(null);
  const { showErrorModal } = useHintModals();
  const { setValue, watch } = useFormContext();
  const directories = watch(DirectoriesField) ?? [];
  const directoryRowId = watch(DirectoryRowIdField) ?? '';
  const isFetchingDirectories = watch(LoadingDirectoriesField) ?? false;

  // updating whenever it's edited from outside the modal or if user cancels
  useEffect(() => {
    setName(Value);
  }, [Value]);

  const getColumns = () => {
    return [{ header: 'Name', accessor: 'name' }];
  };

  const handleRowClick = (row: TableRow) => {
    setName(row.path);
    setSelectedItem(row);
    // NOTE - DirectoryRowIdField defines the field that the parent component will listen to for changes in
    // necessary so it knows when to invoke the fetchDirectories method when a row is pressed
    if (row.isDirectory && row.checked != true) {
      setValue(DirectoryRowIdField, row[IdField]);
    }
  };

  const getFooterButtons = () => {
    return [
      {
        Label: 'Save',
        Visible: true,
        Type: ModalButtonType.Primary,
        OnClick: () => {
          if (IsFile && selectedItem?.isDirectory) {
            showErrorModal({
              Title: 'Error',
              Error: {
                description: 'You must select a file to save, not a directory',
              },
            });
          } else {
            Result(name);
          }
        },
      },
      {
        Visible: true,
        Label: 'Cancel',
        Type: ModalButtonType.Secondary,
        OnClick: Close,
      },
    ];
  };

  const updateDirectories = (data) => {
    setValue(DirectoriesField, data);
  };

  return (
    <Modal Title={`Select ${Title}`} Show Close={Close} FooterButtons={getFooterButtons()}>
      <FormString ReadOnly Label={IsFile ? 'File' : 'Directory'} Value={name} />
      <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
        <SCNestedTable
          emptyLabel="None"
          loadingMessage="Loading directories"
          isLoading={isFetchingDirectories}
          loadingRowId={directoryRowId}
          columns={getColumns()}
          idField={IdField}
          subRowsField={SubRowsField}
          data={directories}
          onRowClick={handleRowClick}
          updateData={updateDirectories}
          expandedIcon={faFolderOpen}
          collapsedIcon={faFolder}
        />
      </div>
    </Modal>
  );
}
